import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { tx, useLang } from "../../lib/i18n";

const box = "w-full rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-3 py-2.5 text-sm outline-none focus-within:border-[var(--mv-teal)]";

// Searchable select with inline "+ Create" — options: [{value, label, hint?}]
export const CreatableSelect = ({ value, options, onChange, onCreate, placeholder = "—", disabled, testid, createLabel = "Create" }) => {
  useLang();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const cur = options.find((o) => o.value === value);
  const s = q.trim().toLowerCase();
  const list = useMemo(() => (s ? options.filter((o) => `${o.label} ${o.hint || ""}`.toLowerCase().includes(s)) : options).slice(0, 80), [options, s]);
  const exact = options.some((o) => o.label.toLowerCase() === s);
  const canCreate = onCreate && s.length > 0 && !exact;
  const items = [...list.map((o) => ({ o })), ...(canCreate ? [{ create: true }] : [])];
  const pick = (o) => { onChange(o.value); setOpen(false); setQ(""); };
  const create = async () => {
    setBusy(true);
    const r = await onCreate(q.trim());
    setBusy(false);
    if (r) { onChange(r); setOpen(false); setQ(""); }
  };
  const key = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const it = items[hi]; if (it?.create) create(); else if (it) pick(it.o); }
    else if (e.key === "Escape") setOpen(false);
  };
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className={`${box} flex items-center gap-2 ${disabled ? "opacity-50" : ""}`} onClick={() => !disabled && setOpen(true)}>
        <input disabled={disabled} value={open ? q : cur?.label || ""} placeholder={tx(cur?.label || placeholder)} onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setHi(0); setOpen(true); }} onKeyDown={key} className="min-w-0 flex-1 bg-transparent outline-none" data-testid={`${testid}-input`} />
        {value && !disabled && <button type="button" aria-label={tx("Clear")} onClick={(e) => { e.stopPropagation(); onChange(""); }} data-testid={`${testid}-clear`}><X className="h-3.5 w-3.5 text-[var(--mv-dim)]" /></button>}
        <ChevronDown className="h-4 w-4 shrink-0 text-[var(--mv-dim)]" />
      </div>
      {open && !disabled && (
        <div className="absolute z-40 mt-1 max-h-64 w-full min-w-[220px] overflow-y-auto rounded-lg border border-[var(--mv-border-2)] bg-[#0b1213] py-1 shadow-xl" data-testid={`${testid}-menu`}>
          {items.length === 0 && <p className="px-3 py-2 text-xs text-[var(--mv-dim)]">{tx(onCreate ? "Type a name to create it" : "No matches")}</p>}
          {items.map((it, i) => it.create ? (
            <button type="button" key="__create" disabled={busy} onMouseEnter={() => setHi(i)} onClick={create} data-testid={`${testid}-create`}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--mv-teal)] ${hi === i ? "bg-[var(--mv-teal-dim)]" : ""}`}>
              <Plus className="h-3.5 w-3.5" />{tx(busy ? "Creating…" : createLabel)} “{q.trim()}”</button>
          ) : (
            <button type="button" key={it.o.value} onMouseEnter={() => setHi(i)} onClick={() => pick(it.o)} data-testid={`${testid}-option-${it.o.value}`}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${hi === i ? "bg-white/[0.06]" : ""}`}>
              <span className="flex-1">{it.o.label}{it.o.hint && <span className="ml-2 text-[11px] text-[var(--mv-dim)]">{it.o.hint}</span>}</span>
              {it.o.value === value && <Check className="h-3.5 w-3.5 text-[var(--mv-teal)]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Free-text chips input with suggestions (tags, option values)
export const ChipsInput = ({ values, onChange, suggestions = [], placeholder, testid }) => {
  useLang();
  const [q, setQ] = useState("");
  const id = `${testid}-dl`;
  const add = (v) => { const t = v.trim(); if (t && !values.includes(t)) onChange([...values, t]); setQ(""); };
  return (
    <div className={`${box} flex flex-wrap items-center gap-1.5`} data-testid={testid}>
      {values.map((v) => (
        <span key={v} className="flex items-center gap-1 rounded-full border border-[var(--mv-teal)]/50 px-2 py-0.5 text-xs text-[var(--mv-teal)]">{v}
          <button type="button" aria-label={tx("Remove")} onClick={() => onChange(values.filter((x) => x !== v))} data-testid={`${testid}-remove-${v.replace(/\W+/g, "-").toLowerCase()}`}><X className="h-3 w-3" /></button></span>
      ))}
      <input list={id} value={q} placeholder={tx(placeholder)} onChange={(e) => setQ(e.target.value)} onBlur={() => q && add(q)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(q); } else if (e.key === "Backspace" && !q && values.length) onChange(values.slice(0, -1)); }}
        className="min-w-[120px] flex-1 bg-transparent text-sm outline-none" data-testid={`${testid}-input`} />
      <datalist id={id}>{suggestions.filter((s) => !values.includes(s)).map((s) => <option key={s} value={s} />)}</datalist>
    </div>
  );
};
